import { apiGet, apiPost } from '../api';
import type { SavedHub } from '../types';
import { esc, timeAgo } from '../util';

/** Render the hub switcher page. */
export function renderHubSwitcher(container: HTMLElement): void {
  container.innerHTML = `
    <div class="switcher-page">
      <h2 class="page-title">Your Hubs</h2>
      <div id="hub-list" class="hub-list">
        <div class="skeleton-card"><div class="skeleton-line"></div><div class="skeleton-line"></div><div class="skeleton-line"></div></div>
        <div class="skeleton-card"><div class="skeleton-line"></div><div class="skeleton-line"></div></div>
      </div>
    </div>
  `;

  loadHubs(container);
}

async function loadHubs(container: HTMLElement): Promise<void> {
  const list = container.querySelector<HTMLElement>('#hub-list');
  if (!list) return;

  try {
    const hubs = await apiGet<SavedHub[]>('/api/hubs');
    list.innerHTML = '';

    if (hubs.length === 0) {
      list.innerHTML = '<p class="empty-text">No saved hubs</p>';
      return;
    }

    for (const hub of hubs) {
      const card = document.createElement('div');
      card.className = `hub-card ${hub.is_active ? 'hub-active' : ''}`;
      const statusDot = hub.is_active ? 'online' : 'offline';
      const truncUrl = hub.url.length > 30 ? hub.url.slice(0, 27) + '...' : hub.url;
      const lastTime = hub.last_connected ? timeAgo(hub.last_connected) : 'never';
      card.innerHTML = `
        <div class="hub-card-header">
          <span class="hub-card-status ${statusDot}"></span>
          <span class="hub-card-name">${esc(hub.name)}</span>
          ${hub.is_active ? '<span class="hub-badge">Active</span>' : ''}
        </div>
        <span class="hub-card-url" title="${esc(hub.url)}">${esc(truncUrl)}</span>
        <span class="hub-card-type">${esc(hub.connection_type)}</span>
        <span class="hub-card-time">Last connected: ${esc(lastTime)}</span>
        <div class="hub-card-actions">
          ${hub.is_active ? '' : `<button class="btn-secondary btn-sm" data-switch="${hub.id}">Switch</button>`}
          <button class="btn-danger btn-sm" data-remove="${hub.id}">Remove</button>
        </div>
      `;
      list.appendChild(card);
    }

    // Switch handlers
    list.querySelectorAll<HTMLButtonElement>('[data-switch]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset['switch'] ?? '';
        await apiPost('/api/hubs/switch', { id });
        loadHubs(container);
      });
    });

    // Remove handlers
    list.querySelectorAll<HTMLButtonElement>('[data-remove]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset['remove'] ?? '';
        await apiPost('/api/hubs/remove', { id });
        loadHubs(container);
      });
    });
  } catch {
    list.innerHTML = '<p class="empty-text">Could not load hubs</p>';
  }
}

